"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogOverlay } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
const galleryImages = [{
  id: 1,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2025/12/625694a6-51b8-4268-b331-191dc768899b-retina-large.jpg",
  alt: "Sizzling fajitas with peppers and onions"
}, {
  id: 2,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2025/12/cbe03a31-6c22-4af7-b7d4-6d6d3e579e0a-retina-large.avif",
  alt: "Loaded nachos with cheese and toppings"
}, {
  id: 3,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/06232e10-afc7-4042-8961-9be8a59c6eac.jpg",
  alt: "Three fresh tacos with various fillings"
}, {
  id: 4,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/798e12e8-79b5-4be8-9bad-e2af7a9e51fb-8d8fa6d6-7124-4411-812b-83ceb12a5b92.png",
  alt: "Enchiladas with red sauce and cheese"
}, {
  id: 5,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/TACOS-TEXAS-MARGARAITAS-C.webp",
  alt: "Warm dining area with festive decor"
}, {
  id: 6,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/MARGARITAS-BEER.webp",
  alt: "Signature margaritas on the rocks"
}, {
  id: 7,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/4fd05bf9-eca8-49cd-9fcd-32e457e4db65-retina-large.jpg",
  alt: "Golden chimichanga with sides"
}, {
  id: 8,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/bebidaas-.webp",
  alt: "Fresh taco salad in crispy bowl"
}, {
  id: 9,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/84782eae-0f7e-4aab-ad1a-45a12a0b02ee-retina-large.avif",
  alt: "Carnitas tacos with cilantro and lime"
}, {
  id: 10,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/f2d2b636-ddb9-4ffc-a24f-34b27b96ddd5-retina-large-2.avif",
  alt: "Bar area with colorful bottles"
}, {
  id: 11,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/26690116-5045-4df5-910a-3585ea297210-d4f2adec-844b-4c2b-90b4-efac800f3799.avif",
  alt: "Shrimp fajitas with vegetables"
}, {
  id: 12,
  src: "https://texasmargaritasmexicanrestaurant.com/wp-content/uploads/2026/01/margaritas-.webp",
  alt: "Family enjoying meal together"
}];
function GalleryCard({
  image,
  onClick,
  index
}: {
  image: typeof galleryImages[0];
  onClick: () => void;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const delay = Math.floor(index / 3) * 200;
  return <div ref={ref} data-index={index} onClick={onClick} className="group cursor-pointer overflow-hidden rounded-lg shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:shadow-medium hover:scale-[1.02]" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: `opacity var(--duration-slow) ease-out ${delay}ms, transform var(--duration-slow) ease-out ${delay}ms`
  }}>
      <div className="relative overflow-hidden bg-muted-light-background">
        <img src={image.src} alt={image.alt} className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105" loading="lazy" />
      </div>
    </div>;
}
export default function PhotoGallery() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
  };
  const goToPrevious = () => {
    setCurrentIndex(prev => prev === 0 ? galleryImages.length - 1 : prev - 1);
  };
  const goToNext = () => {
    setCurrentIndex(prev => prev === galleryImages.length - 1 ? 0 : prev + 1);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      goToPrevious();
    } else if (e.key === "ArrowRight") {
      goToNext();
    } else if (e.key === "Escape") {
      closeLightbox();
    }
  };
  return <section className="bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
            Photo Gallery
          </h2>
          <p className="text-muted-foreground mt-4 text-lg">
            A visual taste of our delicious dishes and welcoming atmosphere
          </p>
        </div>

        <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
          {galleryImages.map((image, index) => <div key={image.id} className="mb-4 break-inside-avoid">
              <GalleryCard image={image} onClick={() => openLightbox(index)} index={index} />
            </div>)}
        </div>
      </div>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogOverlay className="bg-dark-background/90 backdrop-blur-md" />
        <DialogContent showCloseButton={false} className="max-w-[95vw] border-0 bg-transparent p-0 shadow-none sm:max-w-[90vw] lg:max-w-[80vw]" onKeyDown={handleKeyDown}>
          <div className="relative flex items-center justify-center">
            <Button onClick={closeLightbox} variant="ghost" size="icon" className="absolute top-4 right-4 z-50 text-white hover:bg-white/10 focus-ring" aria-label="Close lightbox">
              <X className="size-6" />
            </Button>

            <Button onClick={goToPrevious} variant="ghost" size="icon" className="absolute left-4 z-50 text-white hover:bg-white/10 focus-ring animate-in slide-in-from-left-4" aria-label="Previous image">
              <ChevronLeft className="size-8" />
            </Button>

            <div className="max-h-[90vh] max-w-full overflow-hidden rounded-lg">
              <img src={galleryImages[currentIndex].src} alt={galleryImages[currentIndex].alt} className="h-auto w-full object-contain animate-in fade-in zoom-in-95 duration-300" />
            </div>

            <Button onClick={goToNext} variant="ghost" size="icon" className="absolute right-4 z-50 text-white hover:bg-white/10 focus-ring animate-in slide-in-from-right-4" aria-label="Next image">
              <ChevronRight className="size-8" />
            </Button>
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-white/80">
              {currentIndex + 1} / {galleryImages.length}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>;
}